<template>
    <b-overlay
        :show="showOverlay"
        rounded="sm"
        spinner-variant="primary"
        spinner-small
    >
        <b-row class="pb-2" v-if="showHeading">
            <b-col>
                <h4 v-if="null === id">
                    {{ $t('Page(OrganizationCard):Component(Text):add-new-organization', {type: type}) }}
                </h4>
                <h4 v-else>
                    {{ $t('Page(OrganizationCard):Component(Text):edit-organization-label', {organization: organization.title}) }}
                </h4>
            </b-col>
            <b-col md="6" lg="6">
                <Actions
                    class="float-right"
                    @save="save"
                    @close="close"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <GeneralInfo
                    :type="type"
                    :organization="organization"
                    :is-parent-visible="isParentSelectorVisible"
                    :show-valid-from-to-dates="isInternal"
                    @input="onGeneralInfoChange"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <Contacts
                    :selected="organization.contacts"
                    @save="onContactSave"
                    @remove="onRemoveContact"
                />
            </b-col>
        </b-row>
        <b-row v-if="isProductCategoryComponentVisible">
            <b-col>
                <ProductCategorySelect
                    :title="$t('Component(Text):taric-codes')"
                    @change="onProductCategoryChange"
                    :selected-items="organization.categories"
                />
            </b-col>
        </b-row>
    </b-overlay>
</template>

<script>
import {BRow, BCol, BOverlay} from 'bootstrap-vue'
import {Actions, GeneralInfo} from "@/views/administration/organization/components";
import {ProductCategorySelect} from "@core/components/product";
import Contacts from "@/views/administration/organization/components/Contacts";
import {SupplyChain} from "@core/api";
import {OrganizationType} from "@/libs/supply-chain";
import backendResponse from "@core/mixins/ui/backend-response";

export default {
    mixins: [backendResponse],
    components: {
        BOverlay, BRow, BCol,
        Actions,
        GeneralInfo,
        ProductCategorySelect,
        Contacts,
    },

    props: {
        type: {
            required: true
        },
        id: {
            default: null
        },
        showHeading: {
            default: true
        },
        isRoot: {
            default: false
        }
    },

    data() {
        return {
            showOverlay: true,

            organizations: [],
            organization: {
                title: null,
                state: null,
                domain: null,
                businessRoles: [],
                legalIdentity: null,
                contacts: [],
                categories: [],
                parent: null,
                validFrom: new Date(),
                validTo: new Date('2099-01-01'),
                location: {
                    address: null,
                    city: null,
                    zip: null,
                    coordinates: {
                        latitude: null,
                        longitude: null
                    },
                    unlocode: null
                }
            },
            categories: [],
            contactIdIncrement: 1
        }
    },

    methods: {
        onGeneralInfoChange(info) {
            this.organization.title = info.title;
            this.organization.domain = info.domain;
            this.organization.legalIdentity = info.legalIdentity;
            this.organization.state = info.state;
            this.organization.businessRoles = info.businessRoles;
            this.organization.parent = info.parent;

            if (info.location) {
                this.organization.location = info.location;
            }

            if (this.isInternal) {
                this.organization.validFrom = info.validFrom;
                this.organization.validTo = info.validTo;
            }
        },
        onProductCategoryChange(e) {
            this.categories = e.idList;
        },
        onRemoveContact(contact) {
            let index = this.organization.contacts.findIndex(c => c.id === contact.id);

            if (index >= 0) {
                this.organization.contacts.splice(index, 1);
            }
        },
        onContactSave(id, contact) {
            if (!id) {
                contact.id = (this.contactIdIncrement++) + 'id';
                contact.notPersisted = true;

                this.organization.contacts.push(contact);
            } else {
                let index = this.organization.contacts.findIndex(o => o.id === id);

                if (index !== -1) {
                    let organizationContact = this.organization.contacts[index];
                    organizationContact.name = contact.name;
                    organizationContact.position = contact.position;
                    organizationContact.description = contact.description;
                    organizationContact.communicationChannels = contact.communicationChannels;
                }
            }
        },
        gatherSaveAttributes() {
            let organization = {
                ...this.organization,
                state: this.organization.state.id,
                contacts: this.organization.contacts.map(c => {
                    if (c.notPersisted) {
                        let contact = {...c};
                        delete contact['id'];
                        delete contact['notPersisted'];
                        return contact;
                    }
                    return c;
                })
            };

            if (organization.legalIdentity.type) {
                organization.legalIdentity = {
                    type: organization.legalIdentity.type.id,
                    value: organization.legalIdentity.value
                };
            }

            if (this.type === OrganizationType.INTERNAL) {
                organization.parent = this.organization.parent ? this.organization.parent.id : null;
                organization.businessRoles = this.organization.businessRoles.map(r => r.id);
                organization.categories = this.categories;
            }

            return organization;
        },
        save() {
            this.showOverlay = true;
            let promise = new Promise((resolve, reject) => {
                let organization = this.gatherSaveAttributes();
                switch (this.type) {
                    case OrganizationType.SUPPLIER:
                        this.saveSupplier(organization, resolve, reject);
                        break;

                    case OrganizationType.INTERNAL:
                        this.saveInternalOrganization(organization, resolve, reject);
                        break;

                    case OrganizationType.PRODUCTION_UNIT:
                        this.saveProductionUnit(organization, resolve, reject);
                        break;
                }
            });
            promise
                .then((r) => {
                    if (this.id) {
                        return this.fetch();
                    }

                    if (r.data.id) {
                        this.onCreate(r.data.id);
                    }
                })
                .catch(this.handleBackendError)
                .finally(() => {
                    this.showOverlay = false;
                })
        },
        onCreate(id) {
            const typeRouteMap = {};
            typeRouteMap[OrganizationType.INTERNAL] = 'administration-internal-edit'
            typeRouteMap[OrganizationType.SUPPLIER] = 'administration-supplier-edit'
            typeRouteMap[OrganizationType.PRODUCTION_UNIT] = 'administration-production-unit-edit'

            this.$router.push({
                name: typeRouteMap[this.type],
                params: {type: this.type, id: id}
            });
        },
        close() {
            this.$router.go(-1);
        },
        fetch() {
            return SupplyChain
                .getOrganizationCard(this.id)
                .then(r => {
                    this.organization = r.data;
                });
        },
        saveSupplier(supplier, resolve, reject) {
            if (this.isInternal) {
                delete supplier['validFrom'];
                delete supplier['validTo'];
            }

            if (!this.id) {
                SupplyChain
                    .newSupplier(supplier)
                    .then(r => {
                        this.showDefaultSuccessMessage('Organization has been successfully created');
                        resolve(r);
                    })
                    .catch(reject)
                ;
            } else {
                SupplyChain
                    .updateSupplier(supplier)
                    .then(r => {
                        this.showDefaultSuccessMessage('Organization has been successfully updated');
                        resolve(r);
                    })
                    .catch(reject)
                ;
            }
        },
        saveInternalOrganization(organization, resolve, reject) {
            (this.id
                ? SupplyChain.updateInternalOrganization(organization)
                : SupplyChain.newInternalOrganization(organization))
                .then(r => {
                    this.showDefaultSuccessMessage(
                        this.id ? 'Organization has been successfully updated' : 'Organization has been successfully created'
                    );
                    resolve(r);
                })
                .catch(reject)
        },
        saveProductionUnit(organization, resolve, reject) {
            console.log(organization);
        }
    },

    computed: {
        isInternal() {
            return this.type === OrganizationType.INTERNAL;
        },
        isProductCategoryComponentVisible() {
            return this.type === OrganizationType.INTERNAL;
        },
        isParentSelectorVisible() {
            if (this.type === OrganizationType.INTERNAL) {
                if (!this.organization.id) {
                    return true;
                }
                if (this.organization.parent) {
                    return true;
                }
                return this.organization.parent === null && !this.organization.id;
            }

            return false;
        }
    },

    watch: {
        id() {
            this.fetch().then(() => this.showOverlay = false);
        }
    },

    created() {
        if (this.id) {
            this.fetch().then(() => this.showOverlay = false);
        } else {
            setTimeout(() => this.showOverlay = false, 500);
        }
    }
}
</script>
